import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const TRANSITION_MS = 250;

const focusableSelector = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export default function BottomSheet({ open, onClose, title, children, footer }) {
  const [mounted, setMounted] = useState(open);
  const [visible, setVisible] = useState(false);
  const sheetRef = useRef(null);
  const previousFocusRef = useRef(null);
  const titleId = useId();

  useEffect(() => {
    if (!open) return;

    previousFocusRef.current = document.activeElement;
    setMounted(true);
    const frame = requestAnimationFrame(() => setVisible(true));

    return () => cancelAnimationFrame(frame);
  }, [open]);

  useEffect(() => {
    if (open) return;

    setVisible(false);
    const timeout = setTimeout(() => setMounted(false), TRANSITION_MS);
    return () => clearTimeout(timeout);
  }, [open]);

  useEffect(() => {
    if (!mounted) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [mounted]);

  useEffect(() => {
    if (!visible || !sheetRef.current) return;

    const focusable = sheetRef.current.querySelectorAll(focusableSelector);
    const first = focusable[0] || sheetRef.current;
    first.focus({ preventScroll: true });
  }, [visible]);

  useEffect(() => {
    if (mounted) return;

    const previousFocus = previousFocusRef.current;
    if (previousFocus && typeof previousFocus.focus === 'function') {
      previousFocus.focus({ preventScroll: true });
    }
  }, [mounted]);

  if (!mounted) return null;

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
      return;
    }

    if (event.key !== 'Tab' || !sheetRef.current) return;

    const focusable = Array.from(sheetRef.current.querySelectorAll(focusableSelector));
    if (focusable.length === 0) {
      event.preventDefault();
      sheetRef.current.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return createPortal(
    <>
      <div
        className={`bottom-sheet-backdrop ${visible ? 'is-visible' : ''}`}
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) onClose();
        }}
      >
        <section
          ref={sheetRef}
          className={`bottom-sheet-panel ${visible ? 'is-visible' : ''}`}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          tabIndex={-1}
          onKeyDown={handleKeyDown}
        >
          <div className="bottom-sheet-header">
            <h2 id={titleId}>{title}</h2>
            <button type="button" className="bottom-sheet-close" onClick={onClose} aria-label="Close filter">
              x
            </button>
          </div>
          <div className="bottom-sheet-body">{children}</div>
          {footer && <div className="bottom-sheet-footer">{footer}</div>}
        </section>
      </div>

      <style>{`
        .bottom-sheet-backdrop {
          position: fixed;
          inset: 0;
          z-index: 10000;
          display: flex;
          align-items: flex-end;
          justify-content: center;
          background: rgba(0, 0, 0, 0);
          opacity: 0;
          transition: opacity ${TRANSITION_MS}ms ease-out, background ${TRANSITION_MS}ms ease-out;
        }

        .bottom-sheet-backdrop.is-visible {
          background: rgba(0, 0, 0, 0.5);
          opacity: 1;
        }

        .bottom-sheet-panel {
          width: 100%;
          max-height: 80dvh;
          background: #fff;
          border-radius: 18px 18px 0 0;
          box-shadow: 0 -18px 60px rgba(15, 23, 42, 0.25);
          display: flex;
          flex-direction: column;
          transform: translateY(100%);
          transition: transform ${TRANSITION_MS}ms ease-out;
          outline: none;
        }

        .bottom-sheet-panel.is-visible {
          transform: translateY(0);
        }

        .bottom-sheet-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          padding: 1rem 1rem 0.75rem;
          border-bottom: 1px solid #e5e7eb;
          flex-shrink: 0;
        }

        .bottom-sheet-header h2 {
          margin: 0;
          color: #111827;
          font-size: 1rem;
          font-weight: 700;
          letter-spacing: 0;
        }

        .bottom-sheet-close {
          width: 40px;
          height: 40px;
          border-radius: 999px;
          border: 1px solid #e5e7eb;
          background: #fff;
          color: #111827;
          cursor: pointer;
          font-size: 1.4rem;
          line-height: 1;
        }

        .bottom-sheet-body {
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
          min-height: 0;
        }

        .bottom-sheet-footer {
          flex-shrink: 0;
          padding: 0.75rem 1rem calc(0.75rem + env(safe-area-inset-bottom));
          border-top: 1px solid #e5e7eb;
          background: #fff;
        }

        @media (min-width: 768px) {
          .bottom-sheet-panel {
            max-width: 560px;
            border-radius: 18px 18px 0 0;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .bottom-sheet-backdrop,
          .bottom-sheet-panel {
            transition-duration: 1ms;
          }
        }
      `}</style>
    </>,
    document.body
  );
}
